import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Store } from '../../../store';
import {
    CallRecordingsSearchParams,
    defaultProspectSearchParams,
    defaultRecordingsLogsSearchParams,
    ProspectSearchParams,
} from '../../../data-types/search-params';
import { AlertConfirmable } from 'src/app/decorators/alert-confirmable.decorator';
import { TwilioService } from '../../../services/twilio.service';
import { VoiceContactData } from '../../../data-types/voice-data';
import { AlertService } from '../../../services/alert.service';
import { bufferCount, delay, filter, last, takeUntil, withLatestFrom } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserService } from '../../../services/user.service';
import { DefaultUserData, UserAccountType, UserData } from '../../../data-types/user-data';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { ActionService } from '../../../services/action.service';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { debounce } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import {
    ILeadFormDlgResult,
    LeadFormComponent,
    LeadFormDlgDataParams,
} from 'src/app/leads/lead-form/lead-form.component';
import { ToastrService } from 'ngx-toastr';
import { LeadService } from 'src/app/leads/lead.service';

@Component({
    selector: 'app-calls',
    templateUrl: './calls.component.html',
    styleUrls: ['./calls.component.scss'],
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: '0' })),
            transition('void <=> *', [animate('150ms ease-in')]),
        ]),
        trigger('fadeOut', [
            state('*', style({ opacity: '1' })),
            transition('* <=> void', [animate('100ms ease-out')]),
        ]),
        trigger('fadeInOut', [
            state('void', style({ opacity: '0' })),
            transition('void => *', [animate('150ms ease-out')]),
            state('*', style({ opacity: '1' })),
            transition('* => void', [animate('50ms ease-in')]),
        ]),
    ],
})
export class CallsComponent implements OnInit, OnDestroy {
    isCollapsed = {
        contacts: false,
        recordings: false,
    };
    isLoading = false;

    customerOrProspectItems = [
        { switchName: 'All', isSwitchActive: !defaultProspectSearchParams.prospect, id: 1 },
        { switchName: 'No leads', isSwitchActive: defaultProspectSearchParams.prospect === 0, id: 2 },
    ];

    favoriteOrNot = [
        { switchName: 'Ok', isSwitchActive: defaultProspectSearchParams.favorite === 0, id: 1 },
        { switchName: 'Favorites', isSwitchActive: defaultProspectSearchParams.favorite === 1, id: 2 },
    ];

    bsConfig?: Partial<BsDatepickerConfig> = Object.assign(
        {},
        {
            containerClass: 'theme-default test',
            isAnimated: true,
            dateInputFormat: 'DD/MM/YYYY',
            keepDatepickerOpened: true,
        }
    );

    accountTypes: Array<{ name: string; value: string }> = [];

    @ViewChild('dropDownSelect') dropDownSelect: ElementRef<HTMLElement>;

    prospects: Array<any> = [];
    params: ProspectSearchParams = { ...defaultProspectSearchParams };

    recordingParams: CallRecordingsSearchParams = {
        ...defaultRecordingsLogsSearchParams,
    };

    sound = {};
    playing = null;
    audio = null;

    destroy$ = new Subject<void>();

    user = this.store.selectForLocal('user');
    actionTypes$ = this.actionService.getActionTypes();
    selectedActionTypeName = '';
    @ViewChild('prospectForm', { read: TemplateRef })
    prospectFormTmpl: TemplateRef<any>;

    updateProspectsDebounce;
    private updateProspectsCancel$ = new Subject<Subscription>();

    constructor(
        private store: Store,
        private actionService: ActionService,
        private userService: UserService,
        public twilioService: TwilioService,
        private alertService: AlertService,
        private fb: FormBuilder,
        private activatedRouteSnapshot: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private toastrService: ToastrService,
        private leadSerivce: LeadService
    ) {
        this.recordingParams.phoneNr = this.twilioService.numbersForDomain.map(item => item.phone);
        this.accountTypes = Object.keys(UserAccountType).map((key) => ({
            value: UserAccountType[key],
            name: key,
        }));
        // http://localhost:4200/accounts/calls;newCustomerMob=5555454
        this.activatedRouteSnapshot.params
            .pipe(
                filter((data) => data.hasOwnProperty('newCustomerMob') || data.hasOwnProperty('newCustomerNote')),
                delay(100)
            )
            .subscribe(({ newCustomerMob, newCustomerNote }) => {
                this.router.navigate(router.url.split(';')[0].split('/'), {
                    replaceUrl: true,
                });
                this.addEditLead(null, newCustomerMob, newCustomerNote);
            });
        this.updateProspectsDebounce = debounce(this.updateProspects, 350);
        this.updateProspectsCancel$.pipe(bufferCount(2, 1)).subscribe(([oldSub, newSub]) => oldSub.unsubscribe());
    }

    ngOnInit(): void {
        this.updateProspects();
    }

    updateProspects(param?: { [property in keyof ProspectSearchParams]?: ProspectSearchParams[property] }) {
        this.isLoading = true;
        if (param) {
            this.params = { ...this.params, page: 0, ...param } as ProspectSearchParams;
        }
        this.updateProspectsCancel$.next(
            this.userService.search(this.params).subscribe((result) => {
                this.prospects = result.data.data;
                this.params.pages = Math.ceil(result.data.totalRecords / this.params.limit);
                this.params.totalRecords = result.data.totalRecords;
                this.params.pageArray = [];
                for (let i = 0; i < this.params.pages; i++) {
                    this.params.pageArray.push(i);
                }
                this.isLoading = false;
            })
        );
    }

    @AlertConfirmable({ title: 'Initiate call', message: 'Do you want to make a call?' })
    makeACallProspect(prospect: UserData) {
        if (!this.twilioService.isLineBusy) {
            const contact: VoiceContactData = {
                id: prospect.id,
                accountType: prospect.accountType,
                contactId: prospect.id,
                contactFirstname: prospect.contact.firstname,
                contactSurname: prospect.contact.surname,
                contactMobile: prospect.contact.mobile,
                contactEmail: prospect.contact.email,
            } as VoiceContactData;
            this.twilioService.startOutboundCall(prospect.contact.mobile, contact.id ? contact : null);
        } else {
            this.alertService.error([
                'Line is busy, please nicely finish ongoing conversation before starting the new one.',
            ]);
        }
    }

    async editProspectFav(prospect: UserData) {
        const prospect2Update = { ...DefaultUserData, ...prospect };

        if (this.favoriteOrContact === 0) {
            await this.userService.add2Favorite(prospect2Update).toPromise();
        } else {
            await this.userService.rmFavorite(prospect2Update).toPromise();
        }

        this.updateProspects();
    }

    get favoriteOrContact(): 0 | 1 {
        return this.params.favorite;
    }

    ngOnDestroy(): void {
        if (this.audio) {
            this.audio.pause();
            document.removeChild(this.audio);
            this.audio = null;
        }
        this.destroy$.next();
    }

    addEditLead(leadToEdit = null, initialPhone?: string, initialNote?: string): void {
        let leadFormData = {
            leadToEdit: leadToEdit,
            initialPhone: initialPhone,
            initialNote: initialNote,
        };

        // for new lead
        if (leadToEdit) {
            leadToEdit.firstname = leadToEdit.contact?.firstname;
            leadToEdit.surname = leadToEdit.contact?.surname;
            leadToEdit.phone = leadToEdit.contact?.mobile;
            leadToEdit.email = leadToEdit.contact?.email;
            leadToEdit.customerType = this.leadSerivce.userAccountToCustomerTypeMap[leadToEdit.accountType];
            leadToEdit.companyName = leadToEdit.contact?.companyName;
            leadToEdit.jobTitle = leadToEdit.contact?.jobTitle;
            leadToEdit.contactId = leadToEdit.contact?.id;
            leadToEdit.addressId = leadToEdit.address?.id;
            leadToEdit.country = leadToEdit.address?.country;
            leadToEdit.postcode = leadToEdit.address?.postcode;
            leadToEdit.town = leadToEdit.address?.town;
            leadToEdit.add1 = leadToEdit.address?.add1;
            leadToEdit.add2 = leadToEdit.address?.add2;
            leadToEdit.add3 = leadToEdit.address?.add3;
            leadToEdit.isWarm = leadToEdit.contact?.isWarm;
        }

        const dialogRef = this.dialog.open(LeadFormComponent, {
            ...LeadFormDlgDataParams,
            data: leadFormData,
        });
        const instance = dialogRef.componentInstance;
        if (!leadToEdit) instance.withAction = true;
        dialogRef.afterClosed().subscribe((data: ILeadFormDlgResult) => {
            if (data?.status === true) {
                this.toastrService.success(`Lead ${data.lead.contact.firstname} ${data.lead.contact.surname} saved`);
                this.updateProspects();
            }
        });
    }

    accountTypeName(value: string): string {
        const result = this.accountTypes.find((item) => item.value === value);
        return result ? result.name : '-';
    }

    set accountType(accType: UserAccountType | '') {
        if (this.params.accountType !== accType) {
            this.params.accountType = accType;
            this.updateProspects();
        }
    }

    get UserAccountType(): typeof UserAccountType {
        return UserAccountType;
    }

    get UserAccountTypeEnum() {
        return Object.values(UserAccountType);
    }
}
